import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, BookOpen, ArrowRight, CheckCircle, ShoppingCart, Sparkles, Gamepad2, HelpCircle, BookText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kajabi purchase link
const KAJABI_PURCHASE_URL = "https://vittafinance.mykajabi.com/offers/b5az9R7L";

// Element tiles configuration
const ELEMENT_TILES = [
  { id: 'element-1', title: 'Element 1: Know-Your-Client (KYC) & Suitability', description: 'Client Relationship Model (CRM2), risk tolerance vs risk capacity, Client Focused Reforms (CFRs), Trusted Contact Persons (TCP)', icon: '📘', color: '#22d3ee', gradient: 'linear-gradient(135deg, #22d3ee 0%, #0ea5e9 100%)' },
  { id: 'element-2', title: 'Element 2: Fixed Income', description: 'Bond pricing mechanics, yield calculations, interest rate sensitivity, modified duration, and preferred shares', icon: '📗', color: '#34d399', gradient: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)' },
  { id: 'element-3', title: 'Element 3: Equities', description: 'Common stock structures, short selling rules, margin account mechanics, and retail order types', icon: '📙', color: '#fbbf24', gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' },
  { id: 'element-4', title: 'Element 4: Securities Analysis', description: 'Macroeconomic trends, financial valuation, fundamental and technical analysis, time-value-of-money calculations', icon: '📕', color: '#f87171', gradient: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)' },
  { id: 'element-5', title: 'Element 5: Managed Products', description: 'Mutual Funds, ETFs, Liquid Alternatives - structural differences, cost transparency, and risk profiles', icon: '📓', color: '#a78bfa', gradient: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)' },
  { id: 'element-6', title: 'Element 6: Portfolio Construction', description: 'Modern Portfolio Theory (MPT), strategic and tactical asset allocation, Alpha, Sharpe Ratio, Standard Deviation', icon: '⚖️', color: '#f472b6', gradient: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)' },
  { id: 'element-7', title: 'Element 7: Investment Recommendations', description: 'Building compliant financial prescriptions, bridging client diagnoses with suitable product recommendations', icon: '✅', color: '#818cf8', gradient: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)' },
  { id: 'element-8', title: 'Element 8: Execution', description: 'Trade execution, settlement cycles, and Universal Market Integrity Rules (UMIR) compliance', icon: '🏛️', color: '#2dd4bf', gradient: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)' },
  { id: 'element-9', title: 'Element 9: Monitoring & Reporting', description: 'Ongoing account maintenance, performance benchmarks, red flags, and compliance changes', icon: '📊', color: '#fb923c', gradient: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)' },
  { id: 'mock-exam', title: 'RSE Mock Exam - 120 Questions', description: 'Full-length practice exam covering all 9 elements with detailed diagnostic results', icon: '🎓', color: '#d946ef', gradient: 'linear-gradient(135deg, #d946ef 0%, #c026d3 100%)', isMockExam: true },
];

const RSEInteractiveBook = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  const PRICE = 49.99;
  const ORIGINAL_PRICE = 129.99;

  // Check access on mount if token is in URL
  useEffect(() => {
    if (tokenFromUrl) {
      verifyAccess(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyAccess = async (accessToken, userEmail = '') => {
    setCheckingAccess(true);
    setError('');
    
    try {
      const response = await fetch(
        `${API_URL}/api/books/rse-interactive/verify-access?token=${accessToken}&email=${encodeURIComponent(userEmail)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setHasAccess(true);
          setShowAccessForm(false);
          if (data.first_name) setUserName(data.first_name);
          localStorage.setItem('rse_access_token', accessToken);
          setSearchParams({ token: accessToken });
        } else {
          setError('Invalid access credentials. Please check your email and token.');
        }
      } else {
        setError('Unable to verify access. Please try again.');
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Connection error. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    
    if (!email || !token) {
      setError('Please enter both email and access token.');
      return;
    }
    
    verifyAccess(token, email);
  };

  const handleElementClick = (elementId, isMockExam = false) => {
    if (!hasAccess) return;
    let url;
    if (isMockExam) {
      url = `/rse-mock-exam?token=${tokenFromUrl || token}`;
    } else {
      url = `/books/rse-interactive/${elementId}?token=${tokenFromUrl || token}`;
    }
    window.open(url, '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    }}>
      {/* Hero Section */}
      <div style={{
        padding: '80px 24px 60px',
        maxWidth: '1200px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(34, 211, 238, 0.1)',
          border: '1px solid rgba(34, 211, 238, 0.3)',
          borderRadius: '100px',
          padding: '8px 20px',
          marginBottom: '24px'
        }}>
          <Sparkles size={16} color="#22d3ee" />
          <span style={{ color: '#22d3ee', fontSize: '14px', fontWeight: '600' }}>
            April 2025 Special Pricing
          </span>
        </div>

        <h1 style={{
          fontSize: '56px',
          fontWeight: '900',
          color: 'white',
          marginBottom: '24px',
          lineHeight: '1.1'
        }}>
          RSE Quick Study<br />
          <span style={{ 
            background: 'linear-gradient(135deg, #22d3ee 0%, #34d399 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Interactive Guide
          </span>
        </h1>

        <p style={{
          fontSize: '20px',
          color: '#94a3b8',
          maxWidth: '700px',
          margin: '0 auto 32px',
          lineHeight: '1.6'
        }}>
          Master the RSE exam with Canada's most interactive study experience. 
          9 comprehensive elements plus 120-question mock exam with detailed reading notes, interactive games, flashcards, and exam-style quizzes.
        </p>

        {/* Pricing Display */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{
            fontSize: '24px',
            color: '#64748b',
            textDecoration: 'line-through'
          }}>
            ${ORIGINAL_PRICE.toFixed(2)} CAD
          </div>
          <div style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#22d3ee'
          }}>
            ${PRICE.toFixed(2)} CAD
          </div>
          <div style={{
            background: 'rgba(34, 211, 238, 0.1)',
            border: '1px solid rgba(34, 211, 238, 0.3)',
            borderRadius: '8px',
            padding: '8px 16px',
            color: '#22d3ee',
            fontSize: '16px',
            fontWeight: '700'
          }}>
            Save ${(ORIGINAL_PRICE - PRICE).toFixed(0)}!
          </div>
        </div>

        {!hasAccess && !showAccessForm && (
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                background: 'linear-gradient(135deg, #22d3ee 0%, #34d399 100%)',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: '700',
                textDecoration: 'none',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(34, 211, 238, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={20} />
              Buy Now - ${PRICE.toFixed(2)} CAD
            </a>

            <button
              onClick={() => setShowAccessForm(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: '600',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            >
              <Lock size={20} />
              I Have an Access Code
            </button>
          </div>
        )}

        {showAccessForm && !hasAccess && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'left'
          }}>
            <h3 style={{ color: 'white', fontSize: '24px', marginBottom: '8px' }}>
              Enter Your Access Code
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '24px' }}>
              Your access code will be emailed to you after registration on Kajabi
            </p>

            <form onSubmit={handleAccessSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#cbd5e7', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#22d3ee'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: '#cbd5e7', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Access Token
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter your access token"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#22d3ee'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                />
              </div>

              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  color: '#fca5a5',
                  fontSize: '14px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  disabled={checkingAccess}
                  style={{
                    flex: 1,
                    padding: '14px 24px',
                    background: 'linear-gradient(135deg, #22d3ee 0%, #34d399 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: checkingAccess ? 'not-allowed' : 'pointer',
                    opacity: checkingAccess ? 0.6 : 1,
                    transition: 'opacity 0.2s'
                  }}
                >
                  {checkingAccess ? 'Verifying...' : 'Access Content'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowAccessForm(false);
                    setError('');
                    setEmail('');
                    setToken('');
                  }}
                  style={{
                    padding: '14px 24px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#94a3b8',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {hasAccess && (
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            background: 'rgba(34, 211, 238, 0.1)',
            border: '1px solid rgba(34, 211, 238, 0.3)',
            borderRadius: '16px',
            padding: '24px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <CheckCircle size={32} color="#22d3ee" />
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ color: 'white', fontSize: '18px', marginBottom: '4px' }}>
                Welcome{userName ? `, ${userName}` : ''}! 🎉
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
                You now have full access to all RSE study elements. Click any element below to begin.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Features Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px 60px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '60px'
        }}>
          {[
            { icon: <BookOpen size={24} color="#a78bfa" />, title: 'Reading Notes', desc: 'Comprehensive study notes for each topic' },
            { icon: <Gamepad2 size={24} color="#22d3ee" />, title: 'Interactive Games', desc: 'Learn through engaging games and simulations' },
            { icon: <BookText size={24} color="#34d399" />, title: 'Flashcards', desc: 'Quick review with digital flashcards for key concepts' },
            { icon: <HelpCircle size={24} color="#fbbf24" />, title: 'Exam Quizzes', desc: 'Practice with realistic exam-style questions' },
          ].map((feature, idx) => (
            <div key={idx} style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ marginBottom: '16px' }}>{feature.icon}</div>
              <h3 style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                {feature.title}
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                {feature.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Elements Grid */}
        <h2 style={{
          color: 'white',
          fontSize: '32px',
          fontWeight: '800',
          marginBottom: '32px',
          textAlign: 'center'
        }}>
          Study Elements {hasAccess && '(Click to Access)'}
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '24px'
        }}>
          {ELEMENT_TILES.map((element) => (
            <div
              key={element.id}
              onClick={() => handleElementClick(element.id, element.isMockExam)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '24px',
                cursor: hasAccess ? 'pointer' : 'default',
                transition: 'all 0.3s',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                if (hasAccess) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = element.color;
                  e.currentTarget.style.boxShadow = `0 8px 24px ${element.color}30`;
                }
              }}
              onMouseOut={(e) => {
                if (hasAccess) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: element.gradient
              }} />

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  fontSize: '48px',
                  lineHeight: 1
                }}>
                  {element.icon}
                </div>
                
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '700',
                    marginBottom: '8px',
                    lineHeight: '1.3'
                  }}>
                    {element.title}
                  </h3>
                  
                  <p style={{
                    color: '#94a3b8',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {element.description}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {hasAccess ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: element.color,
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Start Learning
                    <ArrowRight size={16} />
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    <Lock size={16} />
                    Locked
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        {!hasAccess && (
          <div style={{
            marginTop: '80px',
            textAlign: 'center',
            background: 'rgba(34, 211, 238, 0.05)',
            border: '1px solid rgba(34, 211, 238, 0.2)',
            borderRadius: '20px',
            padding: '48px 32px'
          }}>
            <h2 style={{
              color: 'white',
              fontSize: '36px',
              fontWeight: '900',
              marginBottom: '16px'
            }}>
              Ready to Master the RSE Exam?
            </h2>
            
            <p style={{
              color: '#94a3b8',
              fontSize: '18px',
              maxWidth: '600px',
              margin: '0 auto 32px',
              lineHeight: '1.6'
            }}>
              Join thousands of successful candidates who used our interactive study guides. 
              April special pricing ends soon!
            </p>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '20px',
                color: '#64748b',
                textDecoration: 'line-through'
              }}>
                ${ORIGINAL_PRICE.toFixed(2)} CAD
              </div>
              <div style={{
                fontSize: '40px',
                fontWeight: '900',
                color: '#22d3ee'
              }}>
                ${PRICE.toFixed(2)} CAD
              </div>
            </div>

            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                background: 'linear-gradient(135deg, #22d3ee 0%, #34d399 100%)',
                color: 'white',
                padding: '18px 36px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '700',
                textDecoration: 'none',
                boxShadow: '0 4px 20px rgba(34, 211, 238, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={24} />
              Get Started Now
            </a>
          </div>
        )}

        {/* Back to Books */}
        <div style={{ marginTop: '60px', textAlign: 'center' }}>
          <Link
            to="/books"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#94a3b8',
              fontSize: '16px',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#cbd5e7'}
            onMouseOut={(e) => e.currentTarget.style.color = '#94a3b8'}
          >
            <BookOpen size={20} />
            Back to All Study Guides
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RSEInteractiveBook;
